import React, { useEffect, useState } from 'react';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { MENU_BUTTON_SIZE } from '../../../constants';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import ReleaseNotesDrawer from './ReleaseNoteDrawer';
import { Card, PrioSpinner } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import { Button } from '@prio365/prio365-react-library';
import { apiHelpGetReleases } from '../api';
import { ReleaseDto } from '../../../models/Prio365Features';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    height: '100%',
    overflow: 'scroll',
  },
  space: {
    display: 'flex',
  },
  mutedColor: {
    color: theme.old.typography.colors.muted,
  },
  inputFieldContainer: {
    padding: `${theme.old.spacing.unit(3)}px ${theme.old.spacing.unit(3)}px 0`,
  },
  details: {
    float: 'right',
  },
  inputField: {
    marginBottom: theme.old.spacing.baseSpacing,
    '&.ant-input-search > .ant-input-group > .ant-input-group-addon:last-child':
      {
        border: theme.old.borders.content,
        '& .ant-input-search-button': {
          height: '100%',
        },
        '&:hover': {
          borderColor: 'var(--ant-primary-5)',
        },
      },
  },
  listContainer: {
    height: 'calc(100% - 40px)',
    overflowY: 'auto',
  },
  listBorder: {
    '& .ant-list-items': {
      borderBottom: theme.old.borders.content,
    },
  },
  listItem: {
    cursor: 'pointer',
    height: theme.old.spacing.unit(8),
    '-webkit-transition': 'all 0.3s ease-out',
    transition: 'all 0.3s ease-out',
    '&.ant-list-item': {
      padding: `0 ${theme.old.spacing.unit(3)}px`,
      borderTop: theme.old.borders.content,
      borderBottom: 0,
    },
    '& > button': {
      visibility: 'hidden',
    },
    '&:hover': {
      backgroundColor: theme.colors.application.background.default,
    },
    '&:hover > button': {
      visibility: 'visible',
    },
  },
  listItemMeta: {
    '& .ant-list-item-meta-title': {
      display: '-webkit-box',
      '-webkit-line-clamp': 1,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      marginBottom: 0,
      paddingRight: theme.old.spacing.defaultPadding,
    },
  },
  userflowButton: {
    padding: '0!important',
    height: '100%',
    width: MENU_BUTTON_SIZE,
    backgroundColor: theme.old.palette.backgroundPalette.hover.content,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.old.components.table.menuButton.backgroundColor,
      color: theme.old.components.table.menuButton.color,
    },
    '&:focus': {
      backgroundColor: theme.old.components.table.menuButton.backgroundColor,
      color: theme.old.components.table.menuButton.color,
    },
    '&.ant-btn': {
      transition: 'none',
    },
  },
  inlineFloat: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    // p margin auto 0
    '& p': {
      margin: 'auto 0',
    },
  },
  prioNewsButton: {
    margin: '24px 24px 0px 24px',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  errorMessage: {
    padding: theme.old.spacing.unit(3),
    textAlign: 'center',
    color: theme.old.typography.colors.muted,
  },
  releaseActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(1),
  },
  card: {
    overflow: 'hidden',
    backgroundColor: theme.colors.application.background.light,
    minHeight: theme.old.spacing.unit(8),
    '& .prio-card-body': {
      overflowY: 'hidden',
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

interface ZendeskWidgetHelpFormProps {}

export const ZendeskWidgetHelpForm: React.FC<
  ZendeskWidgetHelpFormProps
> = () => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const [subDrawerVisible, setSubDrawerVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [releases, setReleases] = useState<ReleaseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleCardClick = (version: string) => {
    setSelectedVersion(version);
    setSubDrawerVisible(true);
  };

  // Fetch releases using new API
  useEffect(() => {
    const fetchReleases = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, result } = await apiHelpGetReleases();

        if (result.status >= 200 && result.status < 300 && data) {
          const parseToTimestamp = (s?: string): number => {
            if (!s) return 0;
            const m = s.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
            if (m) {
              const day = Number(m[1]);
              const month = Number(m[2]) - 1;
              const year = Number(m[3]);
              return new Date(year, month, day).getTime();
            }
            const parsed = Date.parse(s);
            return isNaN(parsed) ? 0 : parsed;
          };

          const webReleases = (data as ReleaseDto[])
            .filter((r) => !r.platformType || r.platformType === 'Web')
            .slice()
            .sort(
              (a, b) =>
                parseToTimestamp(b.releaseDate) -
                parseToTimestamp(a.releaseDate)
            );

          setReleases(webReleases);
        } else {
          setError(
            t('zendesk:error.loadingReleases') || 'Failed to load releases'
          );
        }
      } catch (err) {
        console.error('Error fetching releases:', err);
        setError(
          t('zendesk:error.loadingReleases') || 'Failed to load releases'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReleases();
  }, [t]);

  // Loading state
  if (loading) {
    return (
      <Flex.Column className={classes.root}>
        <div className={classes.center}>
          <PrioSpinner size="large" />
        </div>
      </Flex.Column>
    );
  }

  // Error state
  if (error) {
    return (
      <Flex.Column className={classes.root}>
        <div className={classes.errorMessage}>
          <p>{error}</p>
          <Button
            type="link"
            onClick={() => window.location.reload()}
            iconProp={['fal', 'refresh']}
          >
            {t('zendesk:retry') || 'Retry'}
          </Button>
        </div>
      </Flex.Column>
    );
  }

  // Empty state
  if (releases.length === 0) {
    return (
      <Flex.Column className={classes.root}>
        <div className={classes.errorMessage}>
          <p>{t('zendesk:noReleases') || 'No releases available'}</p>
        </div>
      </Flex.Column>
    );
  }

  return (
    <Flex.Column
      className={classes.root}
      childrenGap={theme.old.spacing.unit(2)}
    >
      {releases.map((release) => (
        <Card
          key={release.releaseId}
          className={classes.card}
          children={
            <div className={classes.inlineFloat}>
              <div>
                <p>{`${t('zendesk:version')} ${release.version}`}</p>
                {release.title &&
                  release.title !== `Release ${release.version}` && (
                    <p className={classes.mutedColor}>{release.title}</p>
                  )}
              </div>

              <div className={classes.releaseActions}>
                {/* Show user flow button if release has a URL (this would need to be added to your Release model in the future) */}
                {release.releaseUrl && (
                  <div className={classes.inlineFloat}>
                    <p>{t('zendesk:screencast')}</p>
                    <Button
                      className={classes.details}
                      type="link"
                      target="_blank"
                      href={release.releaseUrl}
                      iconProp={['fal', 'play']}
                    />
                  </div>
                )}

                <Button
                  className={classes.details}
                  type="link"
                  onClick={() => handleCardClick(release.version)}
                  iconProp={['fal', 'chevron-right']}
                />
              </div>
            </div>
          }
        />
      ))}

      <ReleaseNotesDrawer
        visible={subDrawerVisible}
        onClose={() => setSubDrawerVisible(false)}
        selectedVersion={selectedVersion}
      />
    </Flex.Column>
  );
};

export default ZendeskWidgetHelpForm;
